import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { getCampaignAttribution } from "./CampaignAttribution";
import {
  sanitizeFunnelProperties,
  type FunnelEventProperties,
  type PersistableFunnelProperties,
} from "./FunnelAnalyticsPrivacy";

export const FUNNEL_EVENTS = {
  homepageCtaClicked: "homepage_cta_clicked",
  homepageFaqOpened: "homepage_faq_opened",
  seoLandingCtaClicked: "seo_landing_cta_clicked",
  authGateViewed: "auth_gate_viewed",
  authGateCtaClicked: "auth_gate_cta_clicked",
  authLoginSucceeded: "auth_login_succeeded",
  authSignupSubmitted: "auth_signup_submitted",
  guestPrecreateViewed: "guest_precreate_viewed",
  guestPrecreateStarted: "guest_precreate_started",
  guestPrecreateReady: "guest_precreate_ready",
  guestPrecreateRestored: "guest_precreate_restored",
  guestGenerateGateExposed: "guest_generate_gate_exposed",
  guestGenerateGateClicked: "guest_generate_gate_clicked",
  imageUploaded: "image_uploaded",
  orientationSelected: "orientation_selected",
  styleSelected: "style_selected",
  generationStarted: "generation_started",
  generationSucceeded: "generation_succeeded",
  generationFailed: "generation_failed",
  postGenerationSupportCtaExposed: "post_generation_support_cta_exposed",
  postGenerationSupportCtaClicked: "post_generation_support_cta_clicked",
  supportViewed: "support_viewed",
  sizeSelected: "size_selected",
  supportSelected: "support_selected",
  supportCheckoutCtaClicked: "support_checkout_cta_clicked",
  orderViewed: "order_viewed",
  shippingQuoteStarted: "shipping_quote_started",
  shippingQuoteSucceeded: "shipping_quote_succeeded",
  shippingQuoteFailed: "shipping_quote_failed",
  paymentButtonClicked: "payment_button_clicked",
  addressValidationFailed: "address_validation_failed",
  checkoutStarted: "checkout_started",
  checkoutRedirected: "checkout_redirected",
  checkoutFailed: "checkout_failed",
  paymentConfirmationStarted: "payment_confirmation_started",
  orderConfirmed: "order_confirmed",
  orderConfirmationFailed: "order_confirmation_failed",
  postGenerationShareCopied: "post_generation_share_copied",
  postGenerationShareOpened: "post_generation_share_opened",
} as const;

export type FunnelEventName = (typeof FUNNEL_EVENTS)[keyof typeof FUNNEL_EVENTS];

type PersistedFunnelEvent = {
  user_id: string | null;
  anonymous_id: string;
  session_id: string;
  event_name: FunnelEventName;
  funnel_step: string;
  step_index: number;
  properties: PersistableFunnelProperties;
  url_path: string | null;
  referrer: string | null;
  campaign_source: string | null;
  campaign_medium: string | null;
  campaign_name: string | null;
  campaign_content: string | null;
  campaign_term: string | null;
};

const ANONYMOUS_ID_KEY = "artify_funnel_anonymous_id";
const SESSION_ID_KEY = "artify_funnel_session_id";

const FUNNEL_STEPS: Record<FunnelEventName, { step: string; index: number }> = {
  homepage_cta_clicked: { step: "visit", index: 10 },
  homepage_faq_opened: { step: "visit", index: 10 },
  seo_landing_cta_clicked: { step: "visit", index: 10 },
  auth_gate_viewed: { step: "auth_gate", index: 20 },
  auth_gate_cta_clicked: { step: "auth_gate_cta", index: 30 },
  auth_signup_submitted: { step: "signup", index: 40 },
  auth_login_succeeded: { step: "login", index: 45 },
  guest_precreate_viewed: { step: "guest_precreate_viewed", index: 50 },
  guest_precreate_started: { step: "guest_precreate", index: 50 },
  guest_precreate_ready: { step: "guest_precreate_ready", index: 60 },
  guest_precreate_restored: { step: "guest_precreate_restored", index: 65 },
  guest_generate_gate_exposed: { step: "guest_generate_gate_exposed", index: 105 },
  guest_generate_gate_clicked: { step: "guest_generate_gate", index: 70 },
  image_uploaded: { step: "image_uploaded", index: 80 },
  orientation_selected: { step: "orientation", index: 90 },
  style_selected: { step: "style", index: 100 },
  generation_started: { step: "generation_started", index: 110 },
  generation_succeeded: { step: "generation_succeeded", index: 120 },
  generation_failed: { step: "generation_failed", index: 120 },
  post_generation_support_cta_exposed: { step: "post_generation_support_cta_exposed", index: 124 },
  post_generation_support_cta_clicked: { step: "post_generation_support_cta_clicked", index: 127 },
  support_viewed: { step: "support_viewed", index: 130 },
  size_selected: { step: "size", index: 140 },
  support_selected: { step: "support", index: 150 },
  support_checkout_cta_clicked: { step: "support_checkout_cta", index: 160 },
  order_viewed: { step: "order_viewed", index: 170 },
  shipping_quote_started: { step: "shipping_quote_started", index: 180 },
  shipping_quote_succeeded: { step: "shipping_quote_succeeded", index: 190 },
  shipping_quote_failed: { step: "shipping_quote_failed", index: 190 },
  payment_button_clicked: { step: "payment_button", index: 200 },
  address_validation_failed: { step: "address_validation", index: 205 },
  checkout_started: { step: "checkout_started", index: 210 },
  checkout_redirected: { step: "checkout_redirected", index: 220 },
  checkout_failed: { step: "checkout_failed", index: 220 },
  payment_confirmation_started: { step: "payment_confirmation_started", index: 230 },
  order_confirmed: { step: "order_confirmed", index: 240 },
  order_confirmation_failed: { step: "order_confirmation_failed", index: 240 },
  post_generation_share_copied: { step: "share", index: 250 },
  post_generation_share_opened: { step: "share", index: 250 },
};

function getBrowserId(storage: Storage, key: string): string {
  const existing = storage.getItem(key);
  if (existing) return existing;

  const generated = crypto.randomUUID();
  storage.setItem(key, generated);
  return generated;
}

function getSafeUrlPath(): string | null {
  if (typeof window === "undefined") return null;
  // Store the route only. Query strings can carry emails, tokens, Stripe/session IDs,
  // or other campaign payloads that should not be persisted in funnel analytics.
  return window.location.pathname.slice(0, 500);
}

function getSafeReferrer(): string | null {
  if (typeof document === "undefined" || !document.referrer) return null;

  try {
    const referrer = new URL(document.referrer);
    if (referrer.origin === window.location.origin) return referrer.pathname.slice(0, 500);
    return referrer.hostname.slice(0, 255);
  } catch {
    return null;
  }
}

async function persistFunnelEvent(name: FunnelEventName, properties: PersistableFunnelProperties) {
  if (typeof window === "undefined") return false;
  if ("onLine" in navigator && !navigator.onLine) return false;

  try {
    const campaign = getCampaignAttribution();
    const step = FUNNEL_STEPS[name];
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const event: PersistedFunnelEvent = {
      user_id: user?.id ?? null,
      anonymous_id: getBrowserId(window.localStorage, ANONYMOUS_ID_KEY),
      session_id: getBrowserId(window.sessionStorage, SESSION_ID_KEY),
      event_name: name,
      funnel_step: step.step,
      step_index: step.index,
      properties,
      url_path: getSafeUrlPath(),
      referrer: getSafeReferrer(),
      campaign_source: campaign.campaign_source ?? null,
      campaign_medium: campaign.campaign_medium ?? null,
      campaign_name: campaign.campaign_name ?? null,
      campaign_content: campaign.campaign_content ?? null,
      campaign_term: campaign.campaign_term ?? null,
    };

    const { error } = await (supabase as unknown as {
      from: (table: string) => { insert: (values: PersistedFunnelEvent) => Promise<{ error: Error | null }> };
    })
      .from("funnel_events")
      .insert(event);

    if (error) {
      if (import.meta.env.DEV) {
        console.warn("[funnel] persistence skipped", error.message);
      }
      return false;
    }
    return true;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("[funnel] persistence failed", error);
    }
    return false;
  }
}

export function trackFunnelEvent(name: FunnelEventName, properties?: FunnelEventProperties) {
  const sanitizedProperties = sanitizeFunnelProperties({
    ...getCampaignAttribution(),
    ...properties,
  }, name);

  Sentry.addBreadcrumb({
    category: "conversion_funnel",
    message: name,
    level: "info",
    data: sanitizedProperties,
  });

  Sentry.setTag("last_funnel_event", name);

  const campaign = getCampaignAttribution();
  Object.entries(campaign).forEach(([key, value]) => {
    if (value) Sentry.setTag(key, value);
  });

  if (import.meta.env.DEV) {
    console.info("[funnel]", name, sanitizedProperties);
  }

  return persistFunnelEvent(name, sanitizedProperties);
}
